import React, { useState, useRef } from "react"
import { X, UploadCloud, Sparkles } from "lucide-react"
import { useAuthFetch } from '@/App'
import GenerationProgress from '@/components/GenerationProgress'

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

type Props = {
    onUpdate: (payload: { updated_files: Record<string, string>, new_files: Record<string, string>, archive_url?: string }) => void
    apiUrl: string,
    archiveUrl: string | null
}

export default function CodeUpdater({ onUpdate, apiUrl, archiveUrl }: Props) {
    const [modalOpen, setModalOpen] = useState(false)
    const [message, setMessage] = useState("")
    const [files, setFiles] = useState<File[]>([])
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Pour la progress
    const [progressState, setProgressState] = useState<"idle" | "waitingForPlan" | "executing">("idle")
    const [currentStep, setCurrentStep] = useState(0)
    const [totalSteps, setTotalSteps] = useState(0)
    const [currentTask, setCurrentTask] = useState("")
    const planRef = useRef<any>(null)

    const authFetch = useAuthFetch()


    const resetGeneration = () => {
        setProgressState('idle')
        setTotalSteps(0)
        setCurrentStep(0)
        setCurrentTask('')
        setUploading(false)

        planRef.current = null
    }


    async function handleStreamingUpdate(payload) {
        setUploading(true)
        setProgressState("waitingForPlan")
        setCurrentTask('Préparation du plan…')
        setError(null)

        try {
            const res = await authFetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })
            if (!res.body) throw new Error("Pas de streaming possible")
            const reader = res.body.getReader()
            const decoder = new TextDecoder()
            let buffer = ""

            setProgressState("executing")

            while (true) {
                const { value, done } = await reader.read()
                if (done) break
                buffer += decoder.decode(value, { stream: true })
                let lines = buffer.split('\n')
                buffer = lines.pop() || ""
                for (let line of lines) {
                    if (!line.trim() || line.trim() === ':\n\n') continue
                    try {
                        const json = JSON.parse(line)
                        if (json.event === "plan_generated" && json.plan) {
                            const subPlan = json.plan.sub_plans?.[0] || json.plan
                            planRef.current = subPlan
                            const metrics = json.plan.metrics
                            const steps = metrics.steps || 0
                            setTotalSteps(steps)
                            setCurrentStep(0)
                            setProgressState('executing')

                        } else if (json.event === "node_start" && json.node_index != null) {
                            // Titre du noeud courant
                            let nodeTitle = ""
                            const flatten = nodes => nodes?.flatMap(n =>
                                [n, ...(n.has_children && n.inputs?.children ? flatten(n.inputs.children) : [])]
                            ) || []
                            const nodes = planRef.current?.plan ? flatten(planRef.current.plan) : []
                            const node = nodes.find(n => n.index === json.node_index)
                            nodeTitle = node?.title || node?.name || ''
                            setCurrentTask(nodeTitle)
                        } else if (json.event === "node_finish" && json.node_index != null) {
                            setCurrentStep(prev => Math.max(prev, json.node_index + 1));
                        } else if (json.event === "project_uploaded" && json.archive_url) {
                            setProgressState("idle")
                            setCurrentStep(0)
                            setTotalSteps(0)
                            setCurrentTask("")
                            setUploading(false);
                            setModalOpen(false);
                            setMessage("");
                            setFiles([]);
                            onUpdate({
                                updated_files: json.updated_files || {},
                                new_files: json.new_files || {},
                                archive_url: json.archive_url
                            })
                            return
                        } else if (json.event === "error") {
                            setError(json.message || "Erreur lors de la mise à jour: " + (json.traceback || ""))
                            setProgressState("idle")
                            setUploading(false)
                        }
                    } catch (e) { }
                }
            }
        } catch (err: any) {
            setError(err.message || "Erreur inconnue de la mise à jour")
            setProgressState("idle")
        }
        finally {
            resetGeneration()
        }
    }

    async function handleSubmit(e) {
        e.preventDefault()
        setError(null)
        const images = []
        for (const file of files) {
            const content = await fileToBase64(file)
            images.push(content)
        }
        const payload = {
            instruction: message,
            images,
            url: archiveUrl
        }
        await handleStreamingUpdate(payload)
    }

    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) return
        setFiles([...files, ...Array.from(e.target.files)])
        e.target.value = ""
    }

    function removeImage(name: string) {
        setFiles(files => files.filter(f => f.name !== name))
    }

    return (
        <>
            <button
                onClick={() => setModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-gradient-to-br from-brixel/90 to-blue-400 hover:from-brixel to-blue-300 text-white font-semibold shadow transition"
                aria-label="Mettre à jour le code"
            >
                <Sparkles className="w-4 h-4" /> Mettre à jour
            </button>
            {modalOpen && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
                    {/* Progress modal ou bien formulaire */}
                    {progressState !== "idle" ? (
                        <div className="relative">
                            <GenerationProgress
                                state={progressState}
                                currentStep={currentStep}
                                totalSteps={totalSteps}
                                currentTask={currentTask}
                                plan={planRef.current}
                            />
                            <button
                                className="absolute top-2 right-2 bg-white rounded-full p-2 hover:bg-gray-100 shadow border"
                                onClick={() => {
                                    setProgressState("idle")
                                    setUploading(false)
                                }}
                                aria-label="Annuler"
                            >
                                <X className="w-5 h-5 text-gray-900" />
                            </button>
                            {error && (
                                <div className="absolute bottom-2 left-0 right-0 text-center text-red-500 font-bold">
                                    {error}
                                </div>
                            )}
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 flex flex-col gap-5 relative"
                        >
                            <button
                                className="absolute top-3 right-3 p-2 bg-gray-100 rounded-full hover:bg-gray-200"
                                type="button"
                                onClick={() => setModalOpen(false)}
                                aria-label="Fermer"
                                disabled={uploading}
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <div>
                                <label className="text-sm font-semibold">Instruction</label>
                                <textarea
                                    rows={4}
                                    placeholder="Expliquez la modification à faire..."
                                    className="border p-2 rounded w-full mt-1 outline-none focus:ring focus:border-brixel text-sm"
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    required
                                    disabled={uploading}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-semibold">Images à importer (optionnel)</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="mt-1"
                                    onChange={handleFileChange}
                                    disabled={uploading}
                                />
                                <div className="mt-2 flex gap-2 flex-wrap">
                                    {files.map(f => (
                                        <div key={f.name} className="relative group">
                                            <span className="absolute -top-2 -right-2 bg-white rounded-full border p-1 cursor-pointer group-hover:bg-red-200"
                                                onClick={() => removeImage(f.name)}>
                                                <X className="w-3 h-3" />
                                            </span>
                                            <img src={URL.createObjectURL(f)} alt={f.name} className="h-12 w-12 object-cover rounded border" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {error && <div className="text-red-500 text-xs text-center">{error}</div>}
                            <button
                                type="submit"
                                disabled={uploading}
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-brixel text-white font-semibold shadow hover:bg-brixel/90 transition mt-2 justify-center"
                            >
                                {uploading ? (
                                    <>
                                        <svg className="animate-spin mr-2 h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        Envoi…
                                    </>
                                ) : (
                                    <>
                                        <UploadCloud className="w-4 h-4" /> Envoyer la demande
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            )}
        </>
    )
}
